import { h, toValue, type Component, type MaybeRefOrGetter } from '#imports';
import {
  parseRangeSelector,
  type ShortcodeRenderer,
} from '@nuxt-shared/lib/cms-shortcodes';
import type { AudioComponentItem, AudioPlayerProps, AudioRecord } from '../types';

const trimmed = (value?: string | null): string => String(value ?? '').trim();

/**
 * Блок `COMPONENTS.audio` ответа API → записи для плеера.
 *
 * Запись без ссылки на файл пропускается, но нумерацию не сдвигает: шорткод
 * адресует позицию в ответе бэкенда.
 *
 * @param fallbackTitle - название, если бэкенд не прислал своё (обычно заголовок материала)
 */
export function parseAudioRecords(
  source?: AudioComponentItem[] | null,
  fallbackTitle = '',
): AudioRecord[] {
  return (source ?? []).reduce<AudioRecord[]>((records, item, position) => {
    const src = trimmed(item?.FILE?.PATH);

    if (src) {
      records.push({
        index: position + 1,
        src,
        title: trimmed(item?.TITLE) || fallbackTitle,
        description: trimmed(item?.DESCRIPTION),
      });
    }

    return records;
  }, []);
}

/** `#audio#` — все записи, `#audio_2-5#` — диапазон, `#audio_3,5#` — перечисление */
const pickAudioRecords = (records: AudioRecord[], params?: string): AudioRecord[] => {
  const selected = parseRangeSelector(params);

  return selected ? records.filter((record) => selected.has(record.index)) : records;
};

const toPlayerProps = ({ src, title, description }: AudioRecord): AudioPlayerProps => ({
  src,
  title,
  meta: description ? [description] : [],
});

/**
 * Рендер шорткода `#audio…#` для `CmsContent`: на каждую выбранную запись — свой
 * плеер. Шорткод, под который записей не нашлось, исчезает из текста.
 *
 * @param AudioPlayerComponent - компонент плеера (передаётся страницей)
 */
export function createAudioShortcode(
  records: MaybeRefOrGetter<AudioRecord[]>,
  AudioPlayerComponent: Component,
): ShortcodeRenderer {
  return (params) =>
    pickAudioRecords(toValue(records), params).map((record) =>
      h(AudioPlayerComponent, { key: record.index, ...toPlayerProps(record) }),
    );
}
